import { useContext, useEffect, useReducer } from 'react'
import {
    Grid,
    UniversalLoadingCircle,
    UniversalModal,
    UniversalToolBar,
    UniversalDataTable,
} from '../../../UniversalComponents'
import {
    Sensors as SensorsIcon,
    Archive as ArchiveIcon,
    Restore,
} from '@mui/icons-material'
import {
    Authentication,
    useAuth,
} from '../../../../context/AuthenticationContext'
import {
    getSignals,
    activateSignals,
    deleteSignals,
    getCDNURLS,
} from '../../../../apiRoutes'
import {
    standardColumnMapper,
    handleActivation,
    handleDeletion,
    permissionsCheck,
} from '../../../../utilities/functionTools'
import SignalManagerForm from './SignalManagerForm'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { ToastMessage } from '../../../../context/ToastPopUpContext'

const today = new Date().toISOString().substring(0, 10)

const SignalManager = ({ restoredData }) => {
    const currentUser = useContext(Authentication)
    const { username, userProfile, activeManufacturer, userPreferences } =
        currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile

    const { setUser } = useAuth()

    const reducer = (state, newState) => ({ ...state, ...newState })
    const defaultState = {
        open: restoredData.id === undefined ? false : true,
        row: restoredData.id === undefined ? {} : restoredData,
        formCache: {},
        data: [],
        defaultIcons: [],
        viewDeactivated: false,
        activateData: [],
        signals: {
            signalManager: {
                columns: [
                    {
                        field: 'signalName',
                        headerName: 'RAW SIGNAL NAME',
                        width: 150,
                        type: 'string',
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 150,
                        type: 'string',
                    },
                    {
                        field: 'signalFriendlyName',
                        headerName: 'SIGNAL NAME',
                        width: 150,
                        type: 'string',
                    },
                    {
                        field: 'signalUnitOfMeasurement.signalUOM',
                        headerName: 'SIGNAL UOM',
                        width: 150,
                        type: 'string',
                    },
                    {
                        field: 'signalPersists',
                        headerName: 'PERSISTENT SIGNAL',
                        width: 150,
                        type: 'boolean',
                    },
                    {
                        field: 'signalIconUrl',
                        headerName: 'SIGNAL ICON URL',
                        width: 150,
                        type: 'string',
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.signals?.signalManager
                ?.density || 'standard',
    }

    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)

    const {
        open,
        row,
        formCache,
        data,
        viewDeactivated,
        activateData,
        defaultIcons,
        signals,
        density,
    } = state

    const { signalManager } = signals

    const { setMinimized } = useMinimizer()

    const getData = async (active: boolean) => {
        let getDefaultIconsData: any = []
        let getSignalsData: any = []
        try {
            getSignalsData = await getSignals(active)
            getDefaultIconsData = await getCDNURLS('signal')
            if (getSignalsData && getSignalsData.length !== 0) {
                const dataMapping = getSignalsData.map((signal) => signal)
                const signals = standardColumnMapper(
                    defaultColumns,
                    dataMapping
                )

                getSignalsData = signals
            }

            if (getDefaultIconsData && getDefaultIconsData?.length === 0) {
                getDefaultIconsData = []
            }
            setState({
                data: getSignalsData,
                defaultIcons: getDefaultIconsData,
            })
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const data =
            userPreferences?.dataTableColumns?.signals?.signalManager?.columns

        if (Array.isArray(data)) {
            const mapColumns = data.map((item) => ({
                field: item?.field,
                headerName: item?.headerName,
                width: item?.width,
                isVisible: item.isVisible,
                type: item?.type,
            }))

            const format = {
                signalManager: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, signals: format })
        }
    }

    const submitColumnData = async () => {
        try {
            setUser({
                key: 'signals',
                value: signals,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivateData = () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'signalFriendlyName',
            setState,
            getData,
            route: activateSignals,
            viewDeactivated,
            setToastMessage,
            page: 'signals',
        })
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'signalFriendlyName',
            setState,
            getData,
            route: deleteSignals,
            viewDeactivated,
            setToastMessage,
            page: 'signals',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these signals?`,
            'warning',
            handleDeletionData
        )
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
        setMinimized(false, restoredData.id, true)
    }, [])

    useEffect(() => {
        getData(!viewDeactivated)
    }, [viewDeactivated, activeManufacturer])

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache.signalFriendlyName
                ? `Edit: ${formCache.signalFriendlyName}`
                : 'Add New Signal',
            link: '/data-hub/signals',
            data: formCache.id
                ? { ...formCache, tab: 'signals' }
                : { ...formCache, id: 'newSignal', tab: 'signals' },
            id: formCache.id ? formCache.id : 'newSignal',
        })
        setState({ open: false })
    }

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.signalFriendlyName) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({ row: defaultState.row, open: !open })
        }
        getData(!viewDeactivated)
    }

    const setViewDeactivated = () =>
        setState({ viewDeactivated: !viewDeactivated })

    const buttons = [
        {
            buttonName: 'Add New Signal',
            buttonIcon: <SensorsIcon />,
            buttonFunction: () => setState({ open: !open }),
            disabled: permissionsCheck(userProfilePermissions, 'Signals'),
        },
        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivateData,
            disabled: permissionsCheck(userProfilePermissions, 'Signals'),
        },
        {
            buttonName: !viewDeactivated
                ? 'View Deactivated Signals'
                : 'View Active Signals',
            buttonIcon: <Restore />,
            buttonFunction: setViewDeactivated,
        },
    ]

    const defaultColumns = [
        'signalFriendlyName',
        'universalSignalList.signalName',
        'signalUnitOfMeasurement.signalUOM',
        'signalIconUrl',
        'manufacturer.manufacturerName',
        'tags',
    ]

    const visibleColumns = [
        'signalFriendlyName',
        'signalName',
        'signalUOM',
        'signalIconUrl',
        'manufacturerName',
        'tags',
    ]

    const customColumnNames = [
        'Signal Name',
        'Signal RAW Name',
        'Signal UOM',
        'Signal Icon URL',
        'Manufacturer',
        'Tags',
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
            type: col.type,
        }))

        const setDensity = state.density

        const setData = {
            signalManager: { columns: format, density: setDensity },
        }

        setState({
            signals: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            signals: {
                signalManager: {
                    ...signalManager,
                    density: newDensity,
                },
            },
        })
    }

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>

            <Grid item xs={12}>
                <UniversalLoadingCircle
                    data={data}
                    reloadFunction={
                        !viewDeactivated
                            ? () => getData(true)
                            : () => getData(false)
                    }
                    customTimeoutMessage={
                        !viewDeactivated
                            ? 'No Active Signals Available'
                            : 'No Deactivated Signals Available'
                    }
                >
                    <UniversalDataTable
                        data={data}
                        apiDensity={density}
                        apiColumns={signals?.signalManager?.columns}
                        savePreferences={() => submitColumnData()}
                        visibleColumns={visibleColumns}
                        customColumnNames={customColumnNames}
                        getRowData={(tableRow) =>
                            setModalVisbility(tableRow, undefined)
                        }
                        onRowSelectionModelChange={(rowSelection) => {
                            setState({ activateData: rowSelection })
                        }}
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                    />
                </UniversalLoadingCircle>
            </Grid>

            <UniversalModal
                title={`${
                    row.signalName
                        ? ` ${row.signalFriendlyName}`
                        : 'Signal Manager'
                }`}
                timeStamp={`${
                    row.updatedBy
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
                onMinimize={minimizeModal}
                draggable={true}
                minimizable={true}
            >
                <SignalManagerForm
                    row={row}
                    formDescription={''}
                    onClose={(tableRow: any, newData: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    saveCache={(data: any) => setState({ formCache: data })}
                    signals={data}
                    permissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Signals'
                    )}
                    defaultIcons={defaultIcons}
                />
            </UniversalModal>
        </Grid>
    )
}

export default SignalManager
